import useResetableStorage from "app/util/useResetableStorage";
import { createContext, useCallback, useContext, useReducer } from "react";

export const ONBOARDING_PHASE = {
  NEW_PLAYER: "NEW_PLAYER",
  STARTED: "STARTED",
  FINISHED: "FINISHED",
} as const;

type OnboardingPhase = (typeof ONBOARDING_PHASE)[keyof typeof ONBOARDING_PHASE];

export interface GameState {
  id?: string;

  onboardingPhase?: OnboardingPhase;

  // Values are indices of the tutorial levels that the player has completed.
  // e.g. If they've completed levels one and three, it will be [0, 2].
  completedTutorialLevels?: Set<string>;

  gameTime: number;
  boardState: string; // Stringified JSON array
  isGameOver: boolean;

  gameStreakComplete?: boolean;

  // True if the daily game data has been started today
  isDailyLevelStarted: boolean;

  // These are changed via the settings menu
  showPlayTime?: boolean;
  isAudioMuted?: boolean;
}

export const FRESH_GAME_STATE: GameState = {
  gameTime: 0,
  boardState: "[]",
  isGameOver: false,
  isDailyLevelStarted: false,
};

export const NEW_PLAYER_STATE: GameState = {
  ...FRESH_GAME_STATE,
  onboardingPhase: ONBOARDING_PHASE.NEW_PLAYER,
  completedTutorialLevels: new Set(),
};

export const GameAction = {
  TUTORIAL_LEVEL_START: "TUTORIAL_LEVEL_START",
  TUTORIAL_LEVEL_FINISH: "TUTORIAL_LEVEL_FINISH",
  DAILY_LEVEL_START: "DAILY_LEVEL_START",
  NEW_GAME_DATA_FOUND: "NEW_GAME",
  GAME_RESET: "GAME_RESET",
  GAME_OVER: "GAME_OVER",
  VIEW_COMPLETED_GRID: "VIEW_COMPLETED_GRID",
  GAME_TIME_INCREMENTED: "GAME_TIME_CHANGED",
  BOARD_STATE_UDPATED: "BOARD_STATE_UPDATED",
  GAME_STREAK_COMPLETED: "GAME_STREAK_COMPLETED",
  AUDIO_MUTE_CHANGED: "AUDIO_MUTE_CHANGED",
  SHOW_PLAY_TIME_CHANGED: "SHOW_PLAY_TIME_CHANGED",
} as const;

export type GameAction =
  | { type: typeof GameAction.TUTORIAL_LEVEL_START }
  | {
      type: typeof GameAction.TUTORIAL_LEVEL_FINISH;
      payload: { levelName: string };
    }
  | { type: typeof GameAction.GAME_RESET }
  | { type: typeof GameAction.NEW_GAME_DATA_FOUND; payload: { gameId: string } }
  | { type: typeof GameAction.DAILY_LEVEL_START }
  | { type: typeof GameAction.GAME_OVER }
  | { type: typeof GameAction.GAME_TIME_INCREMENTED }
  | {
      type: typeof GameAction.BOARD_STATE_UDPATED;
      payload: { newBoardState: string };
    }
  | { type: typeof GameAction.GAME_STREAK_COMPLETED }
  | {
      type: typeof GameAction.AUDIO_MUTE_CHANGED;
      payload: { isMuted: boolean };
    }
  | {
      type: typeof GameAction.SHOW_PLAY_TIME_CHANGED;
      payload: { showPlayTime: boolean };
    };

function gameReducer(state: GameState, action: GameAction) {
  switch (action.type) {
    case GameAction.TUTORIAL_LEVEL_START: {
      return {
        ...state,
        onboardingPhase: ONBOARDING_PHASE.STARTED,
      };
    }
    case GameAction.TUTORIAL_LEVEL_FINISH: {
      const completedLevels =
        state.completedTutorialLevels ?? new Set<string>();

      const newCompletedLevels = new Set([
        ...completedLevels,
        action.payload.levelName,
      ]);

      return {
        ...state,
        completedTutorialLevels: newCompletedLevels,
      };
    }
    case GameAction.GAME_RESET: {
      return {
        ...NEW_PLAYER_STATE,
        isAudioMuted: false,
        showPlayTime: false,
      };
    }
    case GameAction.NEW_GAME_DATA_FOUND: {
      return {
        ...FRESH_GAME_STATE,
        onboardingPhase: state.onboardingPhase ?? ONBOARDING_PHASE.NEW_PLAYER,
        showPlayTime: state.showPlayTime ?? false,
        isAudioMuted: state.isAudioMuted ?? false,
        completedTutorialLevels:
          state.completedTutorialLevels ?? new Set<string>(),
        id: action.payload.gameId,
      };
    }
    case GameAction.DAILY_LEVEL_START: {
      return {
        ...state,
        ...FRESH_GAME_STATE,
        onboardingPhase: ONBOARDING_PHASE.FINISHED,
        isDailyLevelStarted: true,
      };
    }
    case GameAction.GAME_OVER: {
      return {
        ...state,
        isGameOver: true,
      };
    }
    case GameAction.GAME_TIME_INCREMENTED: {
      return {
        ...state,
        gameTime: state.gameTime + 1,
      };
    }
    case GameAction.BOARD_STATE_UDPATED: {
      return {
        ...state,
        boardState: action.payload.newBoardState,
      };
    }
    case GameAction.GAME_STREAK_COMPLETED: {
      return {
        ...state,
        gameStreakComplete: true,
      };
    }
    case GameAction.AUDIO_MUTE_CHANGED: {
      return {
        ...state,
        isAudioMuted: action.payload.isMuted,
      };
    }
    case GameAction.SHOW_PLAY_TIME_CHANGED: {
      return {
        ...state,
        showPlayTime: action.payload.showPlayTime,
      };
    }
    default:
      return state;
  }
}

export function useGameReducer(storageKey = "PIXELLENCE_GAME_STATE") {
  const serializer = (state: GameState): string =>
    JSON.stringify({
      ...state,
      completedTutorialLevels: state.completedTutorialLevels
        ? [...state.completedTutorialLevels]
        : [],
    });

  const deserializer = (value: string): GameState => {
    const parsed = JSON.parse(value);
    return {
      ...parsed,
      onboardingPhase: parsed.onboardingPhase ?? ONBOARDING_PHASE.FINISHED,
      completedTutorialLevels: new Set(parsed.completedTutorialLevels ?? []),
    };
  };

  const [savedState, setSaveState] = useResetableStorage(
    storageKey,
    FRESH_GAME_STATE,
    {
      raw: false,
      serializer,
      deserializer,
    },
  );

  const reducerLocalStorage = useCallback(
    (state: GameState, action: GameAction) => {
      const newState = gameReducer(state, action);
      setSaveState(newState);
      return newState;
    },
    [setSaveState],
  );

  return useReducer(reducerLocalStorage, savedState || FRESH_GAME_STATE);
}

export const GameActionDispatchContext = createContext<
  React.Dispatch<GameAction>
>(() => {});

export function useGameActionDispatch() {
  return useContext(GameActionDispatchContext);
}

export const GameStateContext = createContext<GameState>(FRESH_GAME_STATE);

export default function useGameState() {
  return useContext(GameStateContext);
}
